import {useEffect, useState} from 'react';

import type {ImageSlot} from './bannerForm';
import {imageSlotSrc} from './BannerImagePicker';
import {extractPalette} from './bannerImages';

/**
 * The palette of an image slot. Stored images are on the API origin, so they're
 * fetched as blobs first; a canvas can't read pixels from another origin.
 */
export const useImagePalette = (slot: ImageSlot) => {
  const [palette, setPalette] = useState<string[]>([]);
  const src = imageSlotSrc(slot);
  const isLocal = slot.kind === 'new';

  useEffect(() => {
    if (!src) {
      setPalette([]);
      return;
    }
    let cancelled = false;
    let blobUrl: string | undefined;
    const load = async () => {
      let readable = src;
      if (!isLocal) {
        const response = await fetch(src, {credentials: 'include'});
        if (!response.ok) {
          throw new Error(`Image request failed: ${response.status}`);
        }
        blobUrl = readable = URL.createObjectURL(await response.blob());
      }
      const colors = await extractPalette(readable);
      if (!cancelled) {
        setPalette(colors);
      }
    };
    load()
      .catch(() => !cancelled && setPalette([]))
      .finally(() => blobUrl && URL.revokeObjectURL(blobUrl));
    return () => {
      cancelled = true;
    };
  }, [src, isLocal]);

  return palette;
};
